import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useEffect } from "react";

import Header from "./components/Header";
import Footer from "./components/Footer";

import Home from "./pages/Home";
import Browse from "./pages/Browse";
import ProductLanding from "./pages/ProductLanding";
import ProductCustomize from "./pages/ProductCustomize";
import ProductAccount from "./pages/ProductAccount";
import ProductSchedule from "./pages/ProductSchedule";
import ProductCheckout from "./pages/ProductCheckout";
import ProductShare from "./pages/ProductShare";
import MyEcards from "./pages/MyEcards";
import GiftDetails from "./pages/GiftDetails";
import PredefinedEcardPage from "./pages/PredefinedEcardPage";
import ShortLinkRedirect from "./pages/ShortLinkRedirect";
import Login from "./pages/Login";
import { auth } from "./api/firebase";
import { loadEcardDraft, loadLastShare } from "./constants/giftJourney";
import { isAuthenticated } from "./constants/authSession";

import "./styles/giftAnimations.css";
import "./styles/product.css";
import "./styles/giftDetails.css";
import "./styles/journey.css";

function RedirectHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get("redirect");

    if (redirect) {
      window.history.replaceState(null, "", redirect);
      navigate(redirect, { replace: true });
    }
  }, [navigate]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <RedirectHandler />
      <AppShell />
    </BrowserRouter>
  );
}

function AppShell() {
  const location = useLocation();
  const path = location.pathname;
  const isPublicGiftPath =
    /^\/[^/]+\/[^/]+$/.test(path) &&
    !path.startsWith("/product/") &&
    !path.startsWith("/browse/") &&
    !path.startsWith("/ecard/");
  const hideChrome = isPublicGiftPath || /^\/s\/[^/]+$/.test(path);

  return (
    <div style={appLayout}>
      {!hideChrome ? <Header /> : null}

      <div style={mainContent}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route
            path="/my-ecards"
            element={
              <RequireFlow canAccess={canAccessMyEcardsPage} fallbackPath="/login">
                <MyEcards />
              </RequireFlow>
            }
          />
          <Route path="/browse/:categorySlug" element={<Browse />} />
          <Route path="/ecard/:occasionSlug" element={<PredefinedEcardPage />} />
          <Route
            path="/product/:id/sent"
            element={
              <RequireFlow canAccess={canAccessSharePage} fallbackPath="/">
                <ProductShare />
              </RequireFlow>
            }
          />
          <Route
            path="/product/:id/checkout"
            element={
              <RequireFlow canAccess={canAccessCheckoutPage} fallbackPath="/login">
                <ProductCheckout />
              </RequireFlow>
            }
          />
          <Route
            path="/product/:id/schedule"
            element={
              <RequireFlow canAccess={canAccessSchedulePage} fallbackPath="/">
                <ProductSchedule />
              </RequireFlow>
            }
          />
          <Route path="/product/:id/account" element={<ProductAccount />} />
          <Route path="/product/:id/customize" element={<ProductCustomize />} />
          <Route path="/product/:id" element={<ProductLanding />} />
          <Route path="/s/:shortCode" element={<ShortLinkRedirect />} />
          <Route path="/:categoryId/:giftId" element={<GiftDetails />} />
        </Routes>
      </div>

      {!hideChrome ? <Footer /> : null}
    </div>
  );
}

function RequireFlow({ children, canAccess, fallbackPath }) {
  const location = useLocation();
  const ok = canAccess(location);
  if (ok) return children;
  return <Navigate to={fallbackPath} replace state={{ nextPath: location.pathname }} />;
}

function parseProductId(pathname) {
  const m = pathname.match(/^\/product\/([^/]+)/);
  return m ? m[1] : "";
}

function canAccessSchedulePage(location) {
  const id = parseProductId(location.pathname);
  const draft = loadEcardDraft(id);
  return Boolean(draft?.recipientName);
}

function canAccessCheckoutPage(location) {
  const id = parseProductId(location.pathname);
  const draft = loadEcardDraft(id);
  return Boolean(draft?.recipientName);
}

function canAccessSharePage(location) {
  const id = parseProductId(location.pathname);
  return Boolean(isAuthenticated() && loadLastShare(id));
}

function canAccessMyEcardsPage() {
  return Boolean(auth?.currentUser?.uid);
}

const appLayout = {
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
};

const mainContent = {
  flex: 1,
};

export default App;
